import { Link } from "react-router-dom";
import {
  Home as HomeIcon,
  Building2,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import { usePageMeta } from "@/hooks/usePageMeta";

const RESIDENTIAL_IMAGE = "/images/residentiel.webp";
const COMMERCIAL_IMAGE = "/images/commercial.webp";

export default function Services() {
  const { t } = useLanguage();
  usePageMeta("services");
  const residential = t("services.residential");
  const commercial = t("services.commercial");
  const included = t("services.included");

  return (
    <div className="animate-fade-up">
      {/* HEADER */}
      <section className="py-20 sm:py-28 lg:py-40 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{t("services.eyebrow")}</div>
          <h1 data-testid="services-title" className="font-heading text-5xl lg:text-7xl font-normal tracking-tight leading-[1.15] text-slate-900 mt-4 leading-[1.05] max-w-4xl">
            {t("services.title")}
          </h1>
          <p className="mt-8 text-lg text-slate-600 max-w-2xl leading-relaxed">
            {t("services.subtitle")}
          </p>
        </div>
      </section>

      {/* SEGMENTS */}
      <section className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-8">
          <SegmentCard
            testId="services-residential"
            icon={HomeIcon}
            eyebrow={residential.eyebrow}
            title={residential.title}
            desc={residential.desc}
            image={RESIDENTIAL_IMAGE}
            bullets={residential.bullets}
          />
          <SegmentCard
            testId="services-commercial"
            icon={Building2}
            eyebrow={commercial.eyebrow}
            title={commercial.title}
            desc={commercial.desc}
            image={COMMERCIAL_IMAGE}
            bullets={commercial.bullets}
          />
        </div>
      </section>

      {/* WHAT'S INCLUDED */}
      <section className="py-20 sm:py-28 lg:py-40 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-16">
          <div>
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">{included.eyebrow}</div>
            <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.25] text-slate-900 mt-3 leading-[1.15]">
              {included.title}
            </h2>
            <p className="mt-6 text-slate-600 text-lg leading-relaxed">
              {included.text}
            </p>
            <Link to="/contact" data-testid="services-cta" className="block sm:inline-block">
              <Button className="w-full sm:w-auto mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium whitespace-normal text-center leading-snug">
                {included.cta}
                <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
              </Button>
            </Link>
          </div>
          <ul className="space-y-4">
            {included.items.map((i) => (
              <li key={i} className="flex items-start gap-3 p-5 rounded-xl bg-slate-50 border border-slate-200">
                <CheckCircle2 className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <span className="text-slate-800">{i}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}

function SegmentCard({ testId, icon: Icon, eyebrow, title, desc, image, bullets }) {
  return (
    <div data-testid={testId} className="rounded-2xl overflow-hidden bg-white border border-slate-200">
      <div className="aspect-video overflow-hidden">
        <img src={image} alt={eyebrow} className="w-full h-full object-cover" />
      </div>
      <div className="p-8">
        <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">
          <Icon className="w-4 h-4" /> {eyebrow}
        </div>
        <h3 className="font-heading text-2xl lg:text-3xl font-normal text-slate-900 mt-3">{title}</h3>
        <p className="mt-3 text-slate-600 leading-relaxed">{desc}</p>
        <ul className="mt-6 space-y-2">
          {bullets.map((b) => (
            <li key={b} className="flex items-center gap-2 text-slate-700">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-600" /> {b}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
